import React, { useState, useEffect } from 'react';
import { QueueMonitoringMinimal } from '../components/queue/QueueMonitoringMinimal';
import { QueueMonitoring } from '../components/queue/QueueMonitoring';
import { QueueMonitoringFixed } from '../components/queue/QueueMonitoringFixed';

export const QueueMonitoringTest: React.FC = () => {
  const [activeComponent, setActiveComponent] = useState<'minimal' | 'original' | 'fixed'>('minimal');
  const [apiKey] = useState('test-key');
  const [testResults, setTestResults] = useState<string[]>([]);
  const [isRunningTests, setIsRunningTests] = useState(false);

  // Auto test runner
  const runAutomatedTests = async () => {
    setIsRunningTests(true);
    setTestResults([]);
    
    const addResult = (message: string) => {
      console.log(`[Test] ${message}`);
      setTestResults(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
    };

    try {
      addResult('🚀 Starting automated tests...');
      
      // Test 1: Memory baseline
      if (window.performance?.memory) {
        const memory = window.performance.memory;
        addResult(`📊 Memory baseline: ${(memory.usedJSHeapSize / 1024 / 1024).toFixed(2)}MB`);
      }

      // Test 2: Component switching stress test
      addResult('🔄 Testing component switching...');
      const components: ('minimal' | 'original' | 'fixed')[] = ['minimal', 'fixed', 'minimal', 'fixed'];
      
      for (let i = 0; i < components.length; i++) {
        setActiveComponent(components[i]);
        addResult(`   Switched to: ${components[i]}`);
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        if (window.performance?.memory) {
          const memory = window.performance.memory;
          addResult(`   Memory: ${(memory.usedJSHeapSize / 1024 / 1024).toFixed(2)}MB`);
        }
      }

      // Test 3: API connectivity
      addResult('📡 Testing API endpoints...');
      const endpoints = ['/api/v1/queue/health', '/api/v1/queue/metrics', '/api/v1/queue/backpressure'];
      
      for (const endpoint of endpoints) {
        try {
          const start = performance.now();
          const response = await fetch(endpoint);
          const duration = performance.now() - start;
          addResult(`   ${endpoint}: ${response.status} (${duration.toFixed(0)}ms)`);
        } catch (error) {
          addResult(`   ${endpoint}: Error - ${error}`);
        }
      }

      addResult('✅ All automated tests completed');
      
    } catch (error) {
      addResult(`❌ Test failed: ${error}`);
    } finally {
      setIsRunningTests(false);
    }
  };

  // Memory monitoring
  useEffect(() => {
    const interval = setInterval(() => {
      if (window.performance?.memory) {
        const memory = window.performance.memory;
        const used = memory.usedJSHeapSize / 1024 / 1024;
        
        if (used > 100) { // Alert if over 100MB
          console.warn(`High memory usage: ${used.toFixed(2)}MB`);
        }
      }
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="p-6 space-y-6">
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h1 className="text-xl font-bold text-yellow-800 mb-2">Queue Monitoring Debug & Test Page</h1>
        <p className="text-yellow-700 mb-4">
          Comprehensive testing for Queue Monitoring component infinite loops and performance issues.
        </p>
        
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            onClick={() => setActiveComponent('minimal')}
            className={`px-4 py-2 rounded text-sm font-medium ${
              activeComponent === 'minimal'
                ? 'bg-green-600 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Minimal Test
          </button>
          
          <button
            onClick={() => setActiveComponent('fixed')}
            className={`px-4 py-2 rounded text-sm font-medium ${
              activeComponent === 'fixed'
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Fixed Version
          </button>
          
          <button
            onClick={() => setActiveComponent('original')}
            className={`px-4 py-2 rounded text-sm font-medium ${
              activeComponent === 'original'
                ? 'bg-red-600 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            ⚠️ Original (Problematic)
          </button>
          
          <button
            onClick={runAutomatedTests}
            disabled={isRunningTests}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded text-sm font-medium disabled:opacity-50"
          >
            {isRunningTests ? 'Running Tests...' : 'Run Auto Tests'}
          </button>
        </div>

        <div className="mt-4 text-sm text-yellow-700">
          <strong>Test Strategy:</strong>
          <ol className="list-decimal list-inside mt-2 space-y-1">
            <li><strong>Minimal Test:</strong> Basic React hooks test - if this hangs, fundamental issue</li>
            <li><strong>Fixed Version:</strong> Redesigned component with no infinite loop potential</li>
            <li><strong>Original:</strong> ⚠️ Use carefully - this is the problematic version</li>
            <li><strong>Auto Tests:</strong> Memory usage, component switching, API connectivity</li>
          </ol>
        </div>
      </div>

      {/* Test Results Panel */}
      {testResults.length > 0 && (
        <div className="bg-white border rounded-lg p-4">
          <h3 className="font-bold mb-3">Test Results</h3>
          <div className="bg-gray-100 p-3 rounded font-mono text-xs max-h-40 overflow-y-auto">
            {testResults.map((result, i) => (
              <div key={i} className="mb-1">{result}</div>
            ))}
          </div>
        </div>
      )}

      {/* Active Component Display */}
      <div className="bg-white border rounded-lg p-4">
        <h3 className="font-bold mb-3">
          Active Component: {activeComponent} 
          {isRunningTests && <span className="ml-2 text-sm text-purple-600">(Testing in progress...)</span>}
        </h3>
        
        {activeComponent === 'minimal' && <QueueMonitoringMinimal apiKey={apiKey} />}
        {activeComponent === 'fixed' && <QueueMonitoringFixed apiKey={apiKey} />}
        {activeComponent === 'original' && (
          <div className="border-2 border-red-500 p-2 rounded">
            <div className="bg-red-50 text-red-800 p-2 mb-4 rounded text-sm">
              ⚠️ WARNING: This is the problematic version that may cause browser hangs!
            </div>
            <QueueMonitoring apiKey={apiKey} />
          </div>
        )}
      </div>

      {/* Performance Monitor */}
      <div className="bg-gray-50 border rounded-lg p-4">
        <h3 className="font-bold mb-3">Live Performance Monitor</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <div className="font-medium">Memory Usage</div>
            <div className="text-gray-600">
              {window.performance?.memory 
                ? `${(window.performance.memory.usedJSHeapSize / 1024 / 1024).toFixed(2)}MB`
                : 'Not available'
              }
            </div>
          </div>
          <div>
            <div className="font-medium">Active Component</div>
            <div className="text-gray-600">{activeComponent}</div>
          </div>
          <div>
            <div className="font-medium">Test Status</div>
            <div className="text-gray-600">{isRunningTests ? 'Running' : 'Idle'}</div>
          </div>
          <div>
            <div className="font-medium">Page Load Time</div>
            <div className="text-gray-600">
              {window.performance?.timing 
                ? `${window.performance.timing.loadEventEnd - window.performance.timing.navigationStart}ms`
                : 'Unknown'
              }
            </div>
          </div>
        </div>
      </div>

      {/* Debug Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-bold text-blue-800 mb-2">Debug Instructions</h3>
        <div className="text-blue-700 text-sm space-y-2">
          <div><strong>Console Commands:</strong></div>
          <div className="font-mono bg-blue-100 p-2 rounded">
            <div>window.debugQueue.testAPI() - Test API connectivity</div>
            <div>window.debugQueue.checkMemory() - Check current memory usage</div>
            <div>window.debugQueue.runAll() - Run all diagnostic tests</div>
          </div>
          <div className="mt-3">
            <strong>What to watch for:</strong>
            <ul className="list-disc list-inside mt-1">
              <li>Browser "Page Unresponsive" dialogs</li>
              <li>Memory usage over 100MB</li>
              <li>Console errors about infinite loops</li>
              <li>Repeated renders in React DevTools</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};